import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reportsService } from '../../../services/reportsService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const QuickStatsGrid = () => {
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previousData, setPreviousData] = useState({});
  const [validationResult, setValidationResult] = useState({
    hasValidCustomers: false,
    hasRevenueAccounts: false,
    hasValidData: false
  });
  const [debugInfo, setDebugInfo] = useState({});
  const { user, organizationId } = useAuth();

  useEffect(() => {
    if (user && organizationId) {
      loadStats();
    }
  }, [user, organizationId]);

  const validateRevenueDataPrerequisites = async (organizationId) => {
    try {
      console.debug('🔍 Starting revenue validation for organization:', organizationId);
      
      // Check if we have customers with proper names (strict filtering)
      const { data: customersData, error: customerError } = await supabase
        ?.from('customers')
        ?.select('id, customer_name, status')
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'active') // Only active customers
        ?.not('customer_name', 'is', null)
        ?.neq('customer_name', '')
        ?.neq('customer_name', 'Default')
        ?.neq('customer_name', 'Template')
        ?.neq('customer_name', 'Test');

      if (customerError) {
        console.error('Customer query error:', customerError);
        throw customerError;
      }

      console.debug('📊 Valid customers found:', customersData?.length, customersData);

      // Check if we have revenue accounts properly configured (strict filtering)
      const { data: revenueAccounts, error: accountError } = await supabase
        ?.from('chart_of_accounts')
        ?.select('id, account_name, account_type, account_subtype, status, is_system_account')
        ?.eq('organization_id', organizationId)
        ?.eq('account_type', 'revenue')
        ?.eq('status', 'active')
        ?.eq('is_system_account', false) // Exclude system accounts
        ?.not('account_subtype', 'is', null)
        ?.not('account_name', 'ilike', '%template%')
        ?.not('account_name', 'ilike', '%default%')
        ?.not('account_name', 'ilike', '%test%');

      if (accountError) {
        console.error('Revenue accounts query error:', accountError);
        throw accountError;
      }

      console.debug('💰 Valid revenue accounts found:', revenueAccounts?.length, revenueAccounts);

      // Detailed validation - check for actual invoices with valid customer relationships
      const { data: validInvoices, error: invoiceError } = await supabase
        ?.from('invoices')
        ?.select(`
          id,
          total_amount,
          status,
          customers!inner (
            id,
            customer_name,
            status
          )
        `)
        ?.eq('organization_id', organizationId)
        ?.eq('customers.status', 'active') // Only active customers
        ?.not('customers.customer_name', 'is', null)
        ?.neq('customers.customer_name', '')
        ?.neq('customers.customer_name', 'Default')
        ?.neq('customers.customer_name', 'Template')
        ?.neq('customers.customer_name', 'Test')
        ?.gt('total_amount', 0); // Only invoices with actual amounts

      if (invoiceError) {
        console.error('Invoice validation error:', invoiceError);
        throw invoiceError;
      }

      console.debug('📄 Valid invoices found:', validInvoices?.length, validInvoices);

      const hasValidCustomers = customersData && customersData?.length > 0;
      const hasRevenueAccounts = revenueAccounts && revenueAccounts?.length > 0;
      const hasValidInvoices = validInvoices && validInvoices?.length > 0;
      
      // Enhanced validation - all three conditions must be met
      const hasValidData = hasValidCustomers && hasRevenueAccounts && hasValidInvoices;

      const debugInfo = {
        validCustomers: customersData || [],
        revenueAccounts: revenueAccounts || [],
        validInvoices: validInvoices || [],
        customerCount: customersData?.length || 0,
        revenueAccountCount: revenueAccounts?.length || 0,
        validInvoiceCount: validInvoices?.length || 0
      };
      
      setDebugInfo(debugInfo);
      
      console.debug('✅ Validation result:', {
        hasValidCustomers,
        hasRevenueAccounts, 
        hasValidInvoices,
        hasValidData,
        debugInfo
      });
      
      return {
        hasValidCustomers,
        hasRevenueAccounts,
        hasValidData,
        hasValidInvoices,
        customerCount: customersData?.length || 0,
        revenueAccountCount: revenueAccounts?.length || 0,
        validInvoiceCount: validInvoices?.length || 0,
        debugInfo
      };
    } catch (error) {
      console.error('❌ Error validating revenue data prerequisites:', error);
      return {
        hasValidCustomers: false,
        hasRevenueAccounts: false,
        hasValidData: false,
        hasValidInvoices: false,
        customerCount: 0,
        revenueAccountCount: 0,
        validInvoiceCount: 0,
        debugInfo: {}
      };
    }
  };

  const loadStats = async () => {
    if (!user || !organizationId) return;
    
    setLoading(true);
    setError('');
    
    try {
      if (!organizationId) {
        throw new Error('No organization ID found for user. Please check your account setup.');
      }
      
      console.debug('🚀 Loading stats for organization:', organizationId);
      
      // First validate that we have the necessary data setup
      const validation = await validateRevenueDataPrerequisites(organizationId);
      setValidationResult(validation);

      // Only fetch financial data if validation passes
      if (!validation?.hasValidData) {
        let errorMessage = 'Revenue data cannot be displayed because:\n';
        if (!validation?.hasValidCustomers) {
          errorMessage += `• No active customers with valid names found (found: ${validation?.customerCount || 0})\n`;
        }
        if (!validation?.hasRevenueAccounts) {
          errorMessage += `• No active revenue accounts with proper subtypes found (found: ${validation?.revenueAccountCount || 0})\n`;
        }
        if (!validation?.hasValidInvoices) {
          errorMessage += `• No invoices with valid customer relationships found (found: ${validation?.validInvoiceCount || 0})\n`;
        }
        errorMessage += '\nPlease ensure customer names and account subtypes are properly entered, and at least one invoice exists.';
        
        console.warn('❌ Validation failed:', errorMessage);
        setError(errorMessage);
        setStats([]);
        setLoading(false);
        return;
      }
      
      console.debug('✅ Validation passed, fetching financial overview...');
      
      const financialOverview = await reportsService?.getFinancialOverview(organizationId);
      
      console.debug('📊 Financial overview received:', financialOverview);

      // Additional validation: only show revenue if there are actual invoices with valid customers
      // Use the same strict filtering as in validation
      const { data: validInvoiceData, error: invoiceDataError } = await supabase
        ?.from('invoices')
        ?.select(`
          total_amount, 
          status, 
          issue_date, 
          amount_paid,
          customers!inner (
            customer_name,
            status
          )
        `)
        ?.eq('organization_id', organizationId)
        ?.eq('customers.status', 'active')
        ?.not('customers.customer_name', 'is', null)
        ?.neq('customers.customer_name', '')
        ?.neq('customers.customer_name', 'Default')
        ?.neq('customers.customer_name', 'Template')
        ?.neq('customers.customer_name', 'Test')
        ?.gt('total_amount', 0);

      if (invoiceDataError) {
        throw invoiceDataError;
      }

      // Only show revenue stats if we have invoices with valid customer names
      const hasValidInvoiceData = validInvoiceData && validInvoiceData?.length > 0;
      
      if (!hasValidInvoiceData) {
        setError('Revenue data cannot be displayed because there are no invoices with valid customer names and amounts. Please ensure:\n• Customers have proper names entered\n• Invoices are linked to valid active customers\n• Invoice amounts are greater than zero\n• Account subtypes are configured properly');
        setStats([]);
        setLoading(false);
        return;
      }

      // Calculate revenue only from validated invoices - strict calculation
      const validatedTotalRevenue = validInvoiceData?.reduce((sum, inv) => 
        sum + parseFloat(inv?.total_amount || 0), 0) || 0;

      console.debug('💰 Calculated validated revenue:', validatedTotalRevenue, 'from', validInvoiceData?.length, 'invoices');

      // Calculate actual changes from previous data
      const calculateChange = (current, previous) => {
        if (!previous || previous === 0) return 0;
        return ((current - previous) / Math.abs(previous)) * 100;
      };

      const revenueChange = calculateChange(validatedTotalRevenue, previousData?.totalRevenue);
      const expenseChange = calculateChange(financialOverview?.totalExpenses, previousData?.totalExpenses);
      const netIncomeChange = calculateChange(financialOverview?.netIncome, previousData?.netIncome);
      const outstandingChange = calculateChange(financialOverview?.totalOutstanding, previousData?.totalOutstanding);
      const cashFlowChange = calculateChange(financialOverview?.cashFlow, previousData?.cashFlow);
      const pendingBillsChange = calculateChange(financialOverview?.totalPendingBills, previousData?.totalPendingBills);

      // Update previous data for next calculation
      setPreviousData({
        ...financialOverview,
        totalRevenue: validatedTotalRevenue
      });

      // Transform financial data into stats format with validated data
      const statsData = [
        {
          id: 'total-revenue',
          title: 'Total Revenue (Actual Data Only)',
          value: `$${validatedTotalRevenue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          icon: 'TrendingUp',
          color: 'success',
          change: revenueChange ? `${revenueChange > 0 ? '+' : ''}${revenueChange?.toFixed(1)}%` : 'No data',
          changeType: revenueChange >= 0 ? 'positive' : 'negative',
          period: 'vs previous load',
          lastUpdated: new Date()?.toLocaleString(),
          validationInfo: `Based on ${validInvoiceData?.length} actual invoices with verified customer data`
        },
        {
          id: 'total-expenses',
          title: 'Total Expenses',
          value: `$${financialOverview?.totalExpenses?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          icon: 'ArrowDown',
          color: 'error',
          change: expenseChange ? `${expenseChange > 0 ? '+' : ''}${expenseChange?.toFixed(1)}%` : 'No data',
          changeType: expenseChange <= 0 ? 'positive' : 'negative', // Lower expenses are good
          period: 'vs previous load',
          lastUpdated: new Date()?.toLocaleString()
        },
        {
          id: 'net-income',
          title: 'Net Income (Actual Data Only)',
          value: `$${(validatedTotalRevenue - financialOverview?.totalExpenses)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          icon: 'DollarSign',
          color: (validatedTotalRevenue - financialOverview?.totalExpenses) >= 0 ? 'success' : 'error',
          change: netIncomeChange ? `${netIncomeChange > 0 ? '+' : ''}${netIncomeChange?.toFixed(1)}%` : 'No data',
          changeType: netIncomeChange >= 0 ? 'positive' : 'negative',
          period: 'vs previous load',
          lastUpdated: new Date()?.toLocaleString(),
          validationInfo: 'Calculated from actual revenue data only - no default/template values'
        },
        {
          id: 'outstanding-balance',
          title: 'Outstanding Balance (Actual Data Only)',
          value: `$${financialOverview?.totalOutstanding?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          icon: 'Clock',
          color: 'warning',
          change: outstandingChange ? `${outstandingChange > 0 ? '+' : ''}${outstandingChange?.toFixed(1)}%` : 'No data',
          changeType: outstandingChange <= 0 ? 'positive' : 'negative', // Lower outstanding is good
          period: 'vs previous load',
          lastUpdated: new Date()?.toLocaleString(),
          validationInfo: 'Only includes invoices with valid customer data - no template accounts'
        },
        {
          id: 'cash-flow',
          title: 'Cash Flow',
          value: `$${financialOverview?.cashFlow?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          icon: 'ArrowRightLeft',
          color: financialOverview?.cashFlow >= 0 ? 'primary' : 'error',
          change: cashFlowChange ? `${cashFlowChange > 0 ? '+' : ''}${cashFlowChange?.toFixed(1)}%` : 'No data',
          changeType: cashFlowChange >= 0 ? 'positive' : 'negative',
          period: 'vs previous load',
          lastUpdated: new Date()?.toLocaleString()
        },
        {
          id: 'pending-bills',
          title: 'Pending Bills',
          value: `$${financialOverview?.totalPendingBills?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          icon: 'FileText',
          color: 'accent',
          change: pendingBillsChange ? `${pendingBillsChange > 0 ? '+' : ''}${pendingBillsChange?.toFixed(1)}%` : 'No data',
          changeType: pendingBillsChange <= 0 ? 'positive' : 'negative', // Lower pending bills are good
          period: 'vs previous load',
          lastUpdated: new Date()?.toLocaleString()
        }
      ];
      
      console.debug('📈 Generated stats data:', statsData);
      setStats(statsData);
    } catch (error) {
      console.error('❌ Failed to load financial stats:', error);
      setError('Failed to load financial statistics. Please ensure customer names and account subtypes are properly configured in the database, and no template/default data exists.');
      setStats([]);
    } finally {
      setLoading(false);
    }
  };

  // Add refresh functionality
  const handleRefreshStats = async () => {
    setRefreshing(true);
    
    try {
      await loadStats();
    } catch (error) {
      console.error('Failed to refresh stats:', error);
      setError('Failed to refresh statistics. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleViewDetails = (statId) => {
    // Navigate to detailed view for specific stat
    console.log(`Viewing details for stat ${statId}`);
    // TODO: Implement navigation to detailed report view
  };

  const getColorClasses = (color) => {
    switch (color) {
      case 'success':
        return 'bg-success/10 text-success';
      case 'primary':
        return 'bg-primary/10 text-primary';
      case 'warning':
        return 'bg-warning/10 text-warning';
      case 'error':
        return 'bg-error/10 text-error';
      case 'accent':
        return 'bg-accent/10 text-accent';
      case 'secondary':
        return 'bg-secondary/10 text-secondary';
      default:
        return 'bg-muted/10 text-muted-foreground';
    }
  };

  const getChangeColor = (changeType) => {
    return changeType === 'positive' ? 'text-success' : 'text-error';
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)]?.map((_, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6 animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-muted/50"></div>
              <div className="text-right">
                <div className="w-16 h-4 bg-muted/50 rounded mb-1"></div>
                <div className="w-20 h-3 bg-muted/50 rounded"></div>
              </div>
            </div>
            <div className="space-y-1">
              <div className="w-24 h-3 bg-muted/50 rounded"></div>
              <div className="w-32 h-6 bg-muted/50 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-error/20 rounded-lg p-8">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Revenue Data Validation Failed</h3>
          <div className="text-muted-foreground mb-4 whitespace-pre-line text-left max-w-2xl mx-auto">
            {error}
          </div>
          {!validationResult?.hasValidData && (
            <div className="bg-muted/20 rounded-lg p-4 mb-4 text-left max-w-2xl mx-auto">
              <h4 className="font-semibold text-foreground mb-2">Current Data Analysis:</h4>
              <div className="space-y-2 text-sm">
                <div className={`flex items-center justify-between ${validationResult?.hasValidCustomers ? 'text-success' : 'text-error'}`}>
                  <div className="flex items-center space-x-2">
                    <Icon name={validationResult?.hasValidCustomers ? 'CheckCircle' : 'XCircle'} size={16} />
                    <span>Active Customers:</span>
                  </div>
                  <span className="font-mono">{validationResult?.customerCount || 0}</span>
                </div>
                <div className={`flex items-center justify-between ${validationResult?.hasRevenueAccounts ? 'text-success' : 'text-error'}`}>
                  <div className="flex items-center space-x-2">
                    <Icon name={validationResult?.hasRevenueAccounts ? 'CheckCircle' : 'XCircle'} size={16} />
                    <span>Revenue Accounts:</span>
                  </div>
                  <span className="font-mono">{validationResult?.revenueAccountCount || 0}</span>
                </div>
                <div className={`flex items-center justify-between ${validationResult?.hasValidInvoices ? 'text-success' : 'text-error'}`}>
                  <div className="flex items-center space-x-2">
                    <Icon name={validationResult?.hasValidInvoices ? 'CheckCircle' : 'XCircle'} size={16} />
                    <span>Valid Invoices:</span>
                  </div>
                  <span className="font-mono">{validationResult?.validInvoiceCount || 0}</span>
                </div>
              </div>
              {debugInfo?.validCustomers && debugInfo?.validCustomers?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-muted">
                  <h5 className="text-xs font-semibold text-foreground mb-1">Detected Customers:</h5>
                  <div className="text-xs text-muted-foreground space-y-1">
                    {debugInfo?.validCustomers?.map((customer, index) => (
                      <div key={index} className="font-mono">
                        • {customer?.customer_name} (Status: {customer?.status})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center justify-center space-x-3">
            <Button 
              variant="outline" 
              onClick={loadStats}
              disabled={refreshing}
              iconName={refreshing ? "Loader2" : "RefreshCw"}
            >
              {refreshing ? 'Validating...' : 'Re-validate Data'}
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => console.log('Debug Info:', debugInfo)}
            >
              Log Debug Info
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!stats || stats?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="text-center">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Valid Revenue Data Available</h3>
          <p className="text-muted-foreground mb-4">Revenue statistics can only be displayed when actual customer data and account configurations exist (no template/default values).</p>
          <Button 
            variant="outline" 
            onClick={loadStats}
            disabled={refreshing}
            iconName={refreshing ? "Loader2" : "RefreshCw"}
          >
            {refreshing ? 'Validating...' : 'Check for Actual Data'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {stats?.map((stat) => (
        <div
          key={stat?.id}
          className="bg-card border border-border rounded-lg p-6 hover:shadow-elevated transition-shadow duration-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClasses(stat?.color)}`}>
              <Icon name={stat?.icon || 'DollarSign'} size={24} />
            </div>
            <div className="text-right">
              <div className={`text-sm font-medium ${getChangeColor(stat?.changeType)}`}>
                {stat?.change}
              </div>
              <div className="text-xs text-muted-foreground">{stat?.period}</div>
            </div>
          </div>

          <div className="space-y-1">
            <h3 className="text-sm font-medium text-muted-foreground">{stat?.title}</h3>
            <div className="text-2xl font-bold text-foreground">{stat?.value}</div>
            {stat?.validationInfo && (
              <div className="text-xs text-muted-foreground bg-muted/20 rounded px-2 py-1 mt-2">
                <Icon name="Shield" size={12} className="inline mr-1" />
                {stat?.validationInfo}
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{stat?.lastUpdated || 'No update time available'}</span>
              <button 
                className="hover:text-foreground transition-colors duration-150"
                onClick={() => handleViewDetails(stat?.id)}
                title="View detailed breakdown"
              >
                <Icon name="TrendingUp" size={14} />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs"
                onClick={() => handleViewDetails(stat?.id)}
              >
                View Details
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs"
                onClick={handleRefreshStats}
                disabled={refreshing}
                iconName={refreshing ? "Loader2" : "RefreshCw"}
                iconSize={12}
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default QuickStatsGrid;